import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Users, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { chatApi } from "../lib/realtime";
import { OndaAvatar } from "./OndaAvatar";
import { VerifiedBadge } from "./VerifiedBadge";
import { useNavigate } from "react-router-dom";

export function CreateGroupModal() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]); // list of UserBrief
  const [results, setResults] = useState([]);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  const reset = () => {
    setTitle("");
    setQ("");
    setSelected([]);
    setResults([]);
  };

  useEffect(() => {
    const t = q.trim();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (t.length < 2) {
      setResults([]);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const data = await chatApi.searchUsers(t);
        setResults(data.filter((u) => !selected.some((s) => s.id === u.id)));
      } catch {
        setResults([]);
      }
    }, 320);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q, selected]);

  const add = (u) => {
    setSelected((prev) => [...prev, u]);
    setResults((prev) => prev.filter((r) => r.id !== u.id));
    setQ("");
  };
  const remove = (id) => setSelected((prev) => prev.filter((s) => s.id !== id));

  const submit = async () => {
    const t = title.trim();
    if (!t) {
      toast.error("El título del grupo es obligatorio.");
      return;
    }
    if (t.length > 40) {
      toast.error("El título no puede superar 40 caracteres.");
      return;
    }
    if (selected.length === 0) {
      toast.error("Agrega al menos un miembro");
      return;
    }
    setBusy(true);
    try {
      const { data: conv } = await api.post("/conversations/group", {
        title: t,
        ondatags: selected.map((s) => s.ondatag),
      });
      toast.success(`Grupo "${conv.title}" creado.`);
      reset();
      setOpen(false);
      navigate(`/chat/${conv.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <button
          className="q-btn-ghost inline-flex items-center gap-2 text-xs"
          data-testid="create-group-open"
        >
          <Users size={14} /> Nuevo grupo
        </button>
      </DialogTrigger>
      <DialogContent
        className="border-white/10 bg-[#0f172a]/95 backdrop-blur-2xl text-slate-100 max-w-lg"
        data-testid="create-group-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Crear grupo</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Título del grupo + miembros por <span className="font-mono">@OndaTag</span>.
          </DialogDescription>
        </DialogHeader>

        <label className="block">
          <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
            Título del grupo <span className="text-slate-500 normal-case">({title.length}/40)</span>
          </span>
          <input
            className="q-input mt-1"
            data-testid="group-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 40))}
            maxLength={40}
            placeholder="Ej. Banda Onda"
          />
        </label>

        <div>
          <span className="text-xs uppercase tracking-widest text-slate-400 font-display block mb-1">
            Miembros ({selected.length})
          </span>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="group-selected">
            {selected.map((u) => (
              <span
                key={u.id}
                className="q-chip q-accent-text border-[color:var(--accent)]/40 bg-[color:var(--accent-soft)] font-mono"
                data-testid={`group-selected-${u.ondatag}`}
              >
                @{u.ondatag}
                <button
                  type="button"
                  className="ml-1 hover:text-white"
                  onClick={() => remove(u.id)}
                  aria-label={`Quitar @${u.ondatag}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            {selected.length === 0 && (
              <span className="text-xs text-slate-500 italic">
                Aún no hay miembros.
              </span>
            )}
          </div>

          <input
            className="q-input"
            data-testid="group-search-input"
            placeholder="Busca @OndaTag o correo…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          {results.length > 0 && (
            <ul
              className="mt-2 rounded-xl border border-white/10 bg-black/40 divide-y divide-white/5 max-h-40 overflow-auto"
              data-testid="group-results"
            >
              {results.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => add(u)}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/5"
                    data-testid={`group-result-${u.ondatag}`}
                  >
                    <OndaAvatar src={u.avatar} name={u.display_name} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-100 truncate flex items-center gap-1">
                        {u.display_name}
                        {u.is_verified_business && <VerifiedBadge size={12} />}
                      </div>
                      <div className="font-mono text-xs text-slate-400 truncate">
                        @{u.ondatag}
                      </div>
                    </div>
                    <Plus size={16} className="q-accent-text" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            className="q-btn-ghost"
            onClick={() => setOpen(false)}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="q-btn-primary inline-flex items-center gap-2"
            onClick={submit}
            disabled={busy}
            data-testid="group-create-submit"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Crear grupo
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
